import {
  TicketTypeSerializer as VenueTicketTypeSerializer,
  VenueBasedEventSerializer,
} from "@app-contexts/dashboard/features/events/data/types/events";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { VenueBasedMembershipLevelSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-levels";
import { TicketTypeSerializer as PublicTicketTypeSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import {
  AssignedSeatingModesIdType,
  ASSIGNED_SEATING,
  ASSIGNED_SEATING_MODES,
  common,
} from "@shared/constants";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import {
  PublicMembershipGroupSerializer,
  PublicMembershipLevelSerializer,
} from "@shared/modules/memberships/types";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import get from "lodash/get";
import PublicBasedLocationRepository from "../repositories/PublicBasedLocationRepository";
import PublicBasedSegmentRepository from "../repositories/PublicBasedSegmentRepository";
import PublicBasedSpaceRepository from "../repositories/PublicBasedSpaceRepository";
import PublicBasedTTLocationPermissionsRepository from "../repositories/PublicBasedTTLocationPermissionsRepository";
import PublicBasedTTLocationPermissionsV2Repository from "../repositories/PublicBasedTTLocationPermissionsV2Repository";
import PublicBasedTTSeatPermissionsRepository from "../repositories/PublicBasedTTSeatPermissionsRepository";
import PublicBasedTTSeatPermissionsV2Repository from "../repositories/PublicBasedTTSeatPermissionsV2Repository";
import VenueBasedLocationRepository from "../repositories/VenueBasedLocationRepository";
import VenueBasedSeatPermissionsRepository from "../repositories/VenueBasedSeatPermissionsRepository";
import VenueBasedSegmentRepository from "../repositories/VenueBasedSegmentRepository";
import VenueBasedSpaceRepository from "../repositories/VenueBasedSpaceRepository";
import { SEATING_ITEM_TYPE_MAP } from "../types/seating-property-maps";
import {
  LocationPermissionPropertyMap,
  SeatingEventPropertyMap,
  SeatingItemPropertyMap,
  SeatPermissionPropertyMap,
} from "../types/seating-property-types";
import {
  CartPermissionType,
  FormattedSeatType,
  MapPermissionsType,
  MembershipLocationPermissionType,
  MembershipSeatPermissionType,
  PublicBaseLocationPermissionsV2Serializer,
  PublicBaseLocationSerializer,
  PublicBaseSeatPermissionsV2Serializer,
  PublicBaseSegmentSetSerializer,
  PublicBaseSpaceSerializer,
  PublicTTPermissionsPayloadType,
  PublicTTSeatPermissionSerializer,
  SeatDataType,
  SeatUsageType,
  SeatWithLocationDataType,
  SelectGAType,
  SelectSeatType,
  TicketLocationPermissionType,
  TicketSeatPermissionType,
} from "../types/seating-types";

type itemTypeMapType = {
  [key: number]: SeatingItemPropertyMap;
};

type MapElementMapType = {
  [key: number]: Array<MapPermissionsType>;
};

export default class SeatingService {
  // maps used for quick access to seat data
  // { <itemId>: { colorFill: String, colorStroke: String }, ...  }
  public itemTypeMap: itemTypeMapType = {};

  // map used to get isolated seats. Stores seat ids that are next to a given seat, OR null if the seat is at the end of a row.
  // { <seatId>: { prev: Int | null, next: Int | null }, ... }
  public seatLinkedList: {
    [key: number]: { prev: number | null; next: number | null };
  } = {};

  // stores an array of seat ids associated with each resale submission id
  public resaleMap: { [key: number]: Array<number> } = {}; // { <resaleId>: [array of seat ids], ... }

  public seatingMap: MapElementMapType = {}; // { <seatId>: [{ seatingItemTypeId: Int, seatingItemPermissionId: Int }, ...], ... }

  public locationMap: MapElementMapType = {}; // { <locationId>: [{ id: Int, seatingItemPermissionId: Int, usage_remaining: Int }, ...], ...}

  public segmentsWithPermissions = new Set(); // [segmentId] set of segment IDs, added to if permissions exist on the segment

  public cartPermissions: Array<CartPermissionType> = []; // [{ seatingItemTypeId: Int, seatingItemPermissionId: Int }, ...]

  // List of seats we want to show to the user as selected without actually adding them to the basket
  public pendingCartPermissions: Array<FormattedSeatType> = []; // [{permissionId, minPurchaseLimit, itemType, $data, itemGroupParent}]

  /*
   * Stores the value of purchase limits that override limits on the item types
   * set multiItemsAllowed = true if multiple item types can be added to the pending cart
   *
   * example: dynamic hold limits
   */
  public overridePurchaseLimits:
    | { min: number; max: number; multiItemsAllowed: boolean }
    | undefined = undefined;

  public soldOutSegments: Array<number> = []; // { [segmentId] } array of segment IDS, coming from backend to signify sold out segments

  public advancedSeatColors = false;

  // id of the object containing seating information and the item type array applied to the map
  // example: event id, membership_benefit id
  public seatingObjectId: number | undefined;

  public spaceId: number | undefined;

  public venueId: number | undefined;

  public seatUsages: Array<SeatUsageType> = [];

  public maxQuantityRemaining = -1;

  public mode: AssignedSeatingModesIdType | undefined;

  public locationData = new Map(); // location data by segment

  // flag to prevent multiple network requests when adding seats
  public processing = false;

  public basketHoldID: number | undefined;

  // default basket type is public
  public basketType = common.DataProvider.Public;

  // default map item type is for regular events
  public mapItemType = common.SeatingMapItemType.EVENT;

  // default property map is for regular events
  public seatingObjectPropertyMap: SeatingEventPropertyMap =
    SEATING_ITEM_TYPE_MAP.EVENT.propertyMap;

  // default item property map is for ticket types
  public itemArrayPropertyMap: SeatingItemPropertyMap =
    SEATING_ITEM_TYPE_MAP.EVENT.nestedItemType.propertyMap;

  // default seat permission property map is for ticket permissions
  public seatPermissionPropertyMap: SeatPermissionPropertyMap =
    SEATING_ITEM_TYPE_MAP.EVENT.seatPermissionType.propertyMap;

  // default location permission property map is for ticket permissions
  public locationPermissionPropertyMap: LocationPermissionPropertyMap =
    SEATING_ITEM_TYPE_MAP.EVENT.locationPermissionType.propertyMap;

  // repositories
  public seatPermissionsRepo = new PublicBasedTTSeatPermissionsRepository();

  public seatPermissionsRepoV2 = new PublicBasedTTSeatPermissionsV2Repository();

  public vbSeatPermissionsRepo = new VenueBasedSeatPermissionsRepository();

  public locationPermissionsRepo =
    new PublicBasedTTLocationPermissionsRepository();

  public locationPermissionsRepoV2 =
    new PublicBasedTTLocationPermissionsV2Repository();

  public exchangeSeatPermissions: number[] | string | undefined = [];

  /**
   * @deprecated - should avoid using seatingObjectArray, this field will be refactored into pendingCartPermissions eventually,
   * so should avoid building anything new on this field if possible.
   *
   * */
  public seatingObjectArray: Array<SeatWithLocationDataType> = [];

  constructor() {
    // initialize functions
    this.fetchSpace = this.fetchSpace.bind(this);
    this.initPermissions = this.initPermissions.bind(this);
    this.initBasket = this.initBasket.bind(this);

    // processing functions
    this.isProcessing = this.isProcessing.bind(this);
    this.setProcessing = this.setProcessing.bind(this);

    // seating modal functions
    this.getIsolatedSeats = this.getIsolatedSeats.bind(this);

    // cart permission functions
    this.addToCartPermissions = this.addToCartPermissions.bind(this);
    this.removeFromCartPermissions = this.removeFromCartPermissions.bind(this);
    this.getCartPermission = this.getCartPermission.bind(this);
    this.getPendingCartPermission = this.getPendingCartPermission.bind(this);
    this.getSeatInCartPermissions = this.getSeatInCartPermissions.bind(this);
    this.getSeatInPendingCartPermissions =
      this.getSeatInPendingCartPermissions.bind(this);

    // seat permission functions
    this.resetMultipleSeatPermissions =
      this.resetMultipleSeatPermissions.bind(this);
    this.deleteMultipleSeatPermissions =
      this.deleteMultipleSeatPermissions.bind(this);
    this.deleteSeatPermissions = this.deleteSeatPermissions.bind(this);
    this.sortItemTypeMap = this.sortItemTypeMap.bind(this);
  }

  /**
   * Gets proccessing flag.
   * Used to block multiple seat clicks / network requests.
   */
  public isProcessing(): boolean {
    return this.processing;
  }

  /**
   * Sets processing flag.
   * Used to block multiple seat clicks / network requests.
   */
  public setProcessing(flag: boolean): void {
    this.processing = flag;
  }

  /**
   * Callback function executed when seat is clicked.
   */
  static selectCallBack(
    obj: SelectGAType | SelectSeatType // eslint-disable-line
  ): void | null | Promise<void | null> {
    // default value
    return Promise.resolve();
  }

  /**
   * Callback function executed when multiple seats are clicked.
   */
  static onSeatsSelectedCallback(
    objArray: Array<SeatDataType> // eslint-disable-line
  ): undefined | Array<SeatDataType> | Promise<Array<SeatDataType>> {
    return undefined;
  }

  /**
   * Sets callback function for seat click event.
   */
  static setSelectCallBack(
    onSelect: (
      obj: SelectGAType | SelectSeatType
    ) => void | null | Promise<void | null>
  ): void {
    SeatingService.selectCallBack = onSelect;
  }

  /**
   * Gets callback function for seat click event.
   */
  static getSelectCallBack(): (
    obj: SelectGAType | SelectSeatType
  ) => void | null | Promise<void | null> {
    return SeatingService.selectCallBack;
  }

  /**
   * Gets callback function for multi-seat select event.
   */
  static getSeatsSelectedCallBack(): (
    objArray: Array<SeatDataType>
  ) => undefined | Array<SeatDataType> | Promise<Array<SeatDataType>> {
    return SeatingService.onSeatsSelectedCallback;
  }

  /**
   * Sets callback function for multi-seat selection event.
   */
  static setSeatsSelectedCallback(
    callback: (
      objArray: Array<SeatDataType>
    ) => undefined | Array<SeatDataType> | Promise<Array<SeatDataType>>
  ): void {
    SeatingService.onSeatsSelectedCallback = callback;
  }

  static onSegmentSelectCallback(
    segmentId: number | null // eslint-disable-line
  ): void {
    return undefined;
  }

  static getSegmentSelectCallback(): (segmentId: number | null) => void {
    return SeatingService.onSegmentSelectCallback;
  }

  static setSegmentSelectCallback(
    callback: (segmentId: number | null) => void
  ): void {
    SeatingService.onSegmentSelectCallback = callback;
  }

  /**
   * Formats the seat data for when it is passed to the cart api calls for seat removal/addition
   */
  static formatSeatForCart({
    itemTypeId = undefined,
    permissionId,
    $data,
    itemGroupParent = null,
    minPurchaseLimit = null,
    resaleId,
    quantity = 1, // seat quantities other than 1 are used for waitlisted seats
    payWhatYouCanPrice,
    itemGroupId,
  }: FormattedSeatType): FormattedSeatType {
    return {
      itemTypeId,
      permissionId,
      $data,
      itemGroupParent,
      minPurchaseLimit,
      resaleId,
      quantity,
      payWhatYouCanPrice,
      itemGroupId,
    };
  }

  /**
   * Creates a seatLinkedList for a given row of seats
   * Used to check for isolated seats in situations where the seatLinkedList doesn't exist
   *
   * Example: Large seating maps only create seatLinkedList data for sections that are clicked into.
   * If the page is refreshed and seats are removed from the cart without clicking into the parent section, the seatLinkedList must be recreated.
   */
  createSeatLinkedListForRow(seatDataWithLocation: SeatWithLocationDataType) {
    // add the prev and next seat in the linked list object
    const rowSeats = seatDataWithLocation?.locationData?.seats;
    if (rowSeats) {
      for (let i = 0; i < rowSeats.length; i += 1) {
        this.seatLinkedList[rowSeats[i].id] = {
          prev: i > 0 ? rowSeats[i - 1].id : null,
          next: i < rowSeats.length - 1 ? rowSeats[i + 1].id : null,
        };
      }
    }
  }

  /**
   * Filters seats in seatingServiceState.cartPermissions by a given CartPermission key and value
   *
   * Example: Filtering cartPermissions based on the key "itemTypeId" and an input value for itemTypeId
   * will return all seats in cartPermissions with itemTypeId equal to the input itemTypeId
   *
   */
  filterCartSeatsByProperty(
    cartPermissionKey: keyof CartPermissionType,
    cartPermissionValue: CartPermissionType[typeof cartPermissionKey]
  ): Array<CartPermissionType> {
    const seats: Array<CartPermissionType> = this.cartPermissions.filter(
      (s) => s[cartPermissionKey] === cartPermissionValue && !s.location
    );

    return seats;
  }

  /**
   * Check to see if seat selection is disabled.
   * This should only happen if max quantity remaining is 0;
   */
  isSeatSelectionDisabled(): boolean {
    return this.maxQuantityRemaining === 0;
  }

  /**
   * returns if map is on the box office and the venueId exists
   */
  isVenueMap(): boolean {
    return (
      this &&
      this.mode === ASSIGNED_SEATING_MODES.EVENT_BOX_OFFICE_VIEW.id &&
      Boolean(this.venueId)
    );
  }

  /**
   * returns if map is the item assigner
   */
  isAssignerMap(): boolean {
    return this.mode === ASSIGNED_SEATING_MODES.EDIT_SEAT_TICKETS.id;
  }

  /**
   * returns if map is for group sales
   */
  isGroupSalesMap(): boolean {
    return this.mode === ASSIGNED_SEATING_MODES.GROUP_SALES.id;
  }

  isAttendanceMap(): boolean {
    return this.mode === ASSIGNED_SEATING_MODES.ATTENDANCE.id;
  }

  isModifySeatSelectionMap(): boolean {
    return this.mode === ASSIGNED_SEATING_MODES.MODIFY_SEAT_SELECTION.id;
  }

  /**
   * Set location data to this.locationData map for
   * all segments in the map
   */
  async setAllLocations(
    segments: Array<PublicBaseSegmentSetSerializer>
  ): Promise<void> {
    for (let i = 0; i < segments.length; i += 1) {
      this.locationData.set(segments[i].id, segments[i].location_set);
    }
  }

  /**
   * Fetch space data by id.
   */
  async fetchSpace(spaceId: number): Promise<PublicBaseSpaceSerializer> {
    let space;
    let segmentData;

    const { venueId, isVenueMap } = this;

    const SpaceService = isVenueMap()
      ? new VenueBasedSpaceRepository({ venueId })
      : new PublicBasedSpaceRepository();

    const SegmentService = isVenueMap()
      ? new VenueBasedSegmentRepository({ venueId })
      : new PublicBasedSegmentRepository();

    // Get space object without segments
    // mainly to check the seat count before proceeding
    space = await SpaceService.retrieve(spaceId, {
      params: {
        get_space_only: "on",
        venue_id: venueId,
      },
    });
    // If seat count is under threshold query for the entire space object
    // otherwise, query for the segment data and add it to the space object
    // the former is faster for smaller maps, but the latter will help larger maps
    // be more performant
    if (
      space.seat_count &&
      space.seat_count < ASSIGNED_SEATING.OPTIONS.MAX_COUNT_TO_DISPLAY_ALL_SEATS
    ) {
      const seatCount = space.seat_count; // save value and add to queried space object
      space = await SpaceService.retrieve(spaceId, {
        params: {
          venue_id: venueId,
        },
      });
      space.seat_count = seatCount;

      if (space.segment_set) {
        await this.setAllLocations(space.segment_set);
      }
    } else {
      segmentData = await SegmentService.list({
        params: {
          space: spaceId,
          venue_id: venueId,
        },
      });
      space.segment_set = segmentData.flat();
    }

    return space;
  }

  /**
   * Fetch location data by segment id.
   */
  async fetchSingleLocationData(
    segmentId: number
  ): Promise<Array<PublicBaseLocationSerializer>> {
    let locationData: Array<PublicBaseLocationSerializer> =
      this.locationData.get(segmentId);

    const { spaceId, venueId, isVenueMap } = this;

    const LocationService = isVenueMap()
      ? new VenueBasedLocationRepository({ venueId })
      : new PublicBasedLocationRepository();

    if (!locationData && spaceId) {
      // Previously, we allowed this endpoint to be queried without validating for these params which allowed people
      // to hit the endpoint and get a lot of data returned, this was causing the endpoint to take a long time to
      // return data and eventually crash the pod. So now the backend will enforce that space, segment, and venue_id
      // are all passed in to prevent this issue.
      const promise = await LocationService.list({
        params: {
          space: spaceId,
          venue_id: venueId,
          segment: segmentId,
          get_seats: "on",
        },
      });
      locationData = promise.flat();
      this.locationData.set(segmentId, locationData);
      return locationData;
    }
    return locationData;
  }

  /**
   * Fetch seat permissions.
   * Returns a list of item types with available seats.
   */
  async fetchSeatPermissions(opts?: {
    event?: number;
    membership_benefit?: number;
    customerGroupID?: number;
    basketHoldID?: number;
    exchangeSeatPermissions?: string;
  }): Promise<PublicBaseSeatPermissionsV2Serializer> {
    const exchangeCredit = isStorageAvailable("localStorage")
      ? localStorage.getItem("exchangeCreditData")
      : null;
    const exchangeCreditData = JSON.parse(exchangeCredit || "{}");

    const params = {
      allPermissions: false,
      ...opts,
      exchange_seat_permissions: opts?.exchangeSeatPermissions,
      membership_level_ticket_types:
        exchangeCreditData?.is_membership_ticket_exchange,
    };
    // return all permissions for the item assigner, even for unavailable seats
    if (this.isAssignerMap()) {
      params.allPermissions = true;
    }
    if (this.isAttendanceMap()) {
      if (!this.venueId) {
        throw new Error("Venue ID is required for attendance mode");
      }
      return this.vbSeatPermissionsRepo.getReservedPermissions(this.venueId, {
        params,
      });
    }

    return this.seatPermissionsRepoV2.getPermissions({ params });
  }

  /**
   * Fetch location permissions.
   * Returns a list of item types with available locations.
   */
  async fetchLocationPermissions(opts?: {
    event?: number;
    membership_benefit?: number;
    customerGroupID?: number;
    basketHoldID?: number;
  }): Promise<PublicBaseLocationPermissionsV2Serializer> {
    const exchangeCredit = isStorageAvailable("localStorage")
      ? localStorage.getItem("exchangeCreditData")
      : null;
    const exchangeCreditData = JSON.parse(exchangeCredit || "{}");

    const params = {
      allPermissions: false,
      membership_level_ticket_types:
        exchangeCreditData?.is_membership_ticket_exchange,
      ...opts,
    };
    // return all permissions for the item assigner, even for unavailable locations
    if (this.isAssignerMap()) {
      params.allPermissions = true;
    }

    return this.locationPermissionsRepoV2.getPermissions({ params });
  }

  /**
   * Creates a map for the items assigned to the map (ticket types, membership levels, etc...).
   * Used for quick access to item data.
   */
  createItemTypeMap(itemTypes: Array<SeatingItemPropertyMap>): itemTypeMapType {
    /**
     * Create array of seating colors from constants.
     * Used to assign colors to each seat permission.
     */
    const seatingColors = Object.values(ASSIGNED_SEATING.TICKET_TYPE_COLORS);

    /**
     * Reduce array of item types to a map of item types.
     * The value of each itemType.id is item type styles.
     *
     * itemTypeMap: {
     *   <itemTypeId>: {
     *     colorFill: String,
     *     colorStroke: String
     *   },
     *   ...
     * }
     */
    const getCountOptions = (
      minPurchaseLimit: number,
      maxPurchaseLimit: number
    ): Array<number> => {
      const optionsArr = [];
      for (let i = minPurchaseLimit; i < maxPurchaseLimit + 1; i += 1) {
        optionsArr.push(i);
      }
      // if there is a minimum purchase limit, include 0 as the first option
      if (!optionsArr.includes(0)) {
        optionsArr.unshift(0);
      }
      return optionsArr;
    };

    const itemTypeMap: itemTypeMapType = itemTypes
      // sort by sort_order OR id in order to get consistent colors for seats
      .sort((a, b) => SeatingService.sortPermissions(a, b, this.mapItemType))
      .reduce(
        (acc: itemTypeMapType, curr: SeatingItemPropertyMap, idx: number) => {
          const {
            id,
            sold_out,
            waitlist,
            minimum_purchase_limit,
            purchase_limit,
            seat_permission_color,
          } = curr;

          // return early if the item already exists in the map object being generated
          // or if the item isn't available for selection
          const isAvailable = Boolean(
            this.isGroupSalesMap() ||
              this.isAttendanceMap() ||
              !sold_out ||
              waitlist ||
              this.basketHoldID
          );

          if (acc[id] || !isAvailable) {
            return acc;
          }

          // Get custom seat color, or select from default list
          const colorFill =
            seat_permission_color ??
            seatingColors[idx % seatingColors.length].colorFill;

          const itemType: SeatingItemPropertyMap = {
            ...curr,
            colorFill,
            $countOptions: getCountOptions(
              minimum_purchase_limit ?? 0, // default min limit is 0
              purchase_limit ?? 8 // default max limit is 8
            ),
          };
          return { ...acc, [id]: itemType };
        },
        {}
      );

    this.itemTypeMap = itemTypeMap;
    return this.itemTypeMap;
  }

  /**
   * Creates a map indexed by resale ids.
   * Used for quick access to seat ids associated with a resale id.
   */
  createResaleMap(resaleId: number, seatId: number): void {
    // if the resale id doesn't exist in the map, create a new entry
    // if the seat array doesn't contain a given id, add the id
    if (
      !this.resaleMap[resaleId] ||
      !this.resaleMap[resaleId].includes(seatId)
    ) {
      this.resaleMap = {
        ...this.resaleMap,
        [resaleId]: [
          ...(this.resaleMap[resaleId] ?? []), // initialize entry to empty array if it's undefined
          seatId,
        ],
      };
    }
  }

  /**
   * Creates a seat map.
   * Used for quick access to seat data.
   */
  createSeatPermissionMap(
    permissions: Array<{ id: number; seats: Array<SeatPermissionPropertyMap> }>,
    itemMap: itemTypeMapType
  ): void {
    if (!permissions) {
      return;
    }
    /**
     * Reduce array of item types to a map of seats.
     * The value of each seat.id is an array of item types.
     *
     * seatMap: {
     *   <seatId>: [
     *     { seatingItemTypeId: Int, seatingItemPermissionId: Int },
     *     ...
     *   ],
     *   ...
     * }
     */

    const seatingMap: MapElementMapType = permissions.reduce(
      (acc: MapElementMapType, curr) => {
        curr.seats.forEach((seat) => {
          const { id } = curr;
          // make sure permission is available
          if (!itemMap[id]) {
            return;
          }
          // add entries to the resaleMap
          if (seat.resaleId) {
            this.createResaleMap(seat.resaleId, seat.seatId);
          }
          const permissions = {
            seatingItemPermissionId: seat.permissionId,
            seatingItemTypeId: id,
            seatingItemPasswordProtected: itemMap[id].is_password_protected,
            seatingItemMinPurchaseLimit: itemMap[id].minimum_purchase_limit,
            seatingItemResaleId: seat.resaleId,
            seatingItemResaleStatus: seat.resaleSubmissionStatus,
            seatPurchaser: seat.basket,
            seatStatus: seat.status,
          };
          // update active segment set
          if (seat.segmentId) {
            this.segmentsWithPermissions.add(seat.segmentId);
          }

          if (!acc[seat.seatId]) {
            acc[seat.seatId] = [permissions];
          } else {
            // if the seat already has a resale permission,
            // don't add any more permissions to it
            if (acc[seat.seatId].find((p) => p.seatingItemResaleId)) {
              return;
            }
            // if the incoming permission has a resale id,
            // overwrite all of the other permissions with it
            if (permissions.seatingItemResaleId) {
              acc[seat.seatId] = [permissions];
              return;
            }
            // default: add new permissions to the existing permissions array
            acc[seat.seatId] = acc[seat.seatId].concat(permissions);
          }
        });
        return acc;
      },
      {}
    );
    this.seatingMap = seatingMap;
  }

  /**
   * Creates a seat map.
   * Used for quick access to seat data.
   */
  createLocationPermissionMap(
    permissions: Array<{
      id: number;
      locations: Array<LocationPermissionPropertyMap>;
    }>,
    itemMap: itemTypeMapType
  ): void {
    if (!permissions) {
      return;
    }
    /**
     * Reduce array of item types to a map of seats.
     * The value of each seat.id is an array of item types.
     *
     * locationMap: {
     *   <locationId>: [
     *     { seatingItemTypeId: Int, seatingItemPermissionId: Int, usage_remaining: Int },
     *     ...
     *   ],
     *   ...
     * }
     */
    const locationMap: MapElementMapType = permissions.reduce(
      (acc: MapElementMapType, curr) => {
        const { id } = curr;

        if (!itemMap[id]) {
          return acc;
        }

        curr.locations.forEach((location) => {
          const permissions = {
            seatingItemPermissionId: location.permissionId,
            seatingItemTypeId: id,
            usage_remaining: location.usageRemaining,
            seatingItemPasswordProtected: itemMap[id].is_password_protected,
            capacity_limit: location.capacityLimit,
            location: location.locationId,
            location_capacity: location.locationCapacity,
            locationPendingResaleCount: location.locationPendingResaleCount,
          };
          // update active segment set
          if (location.segmentId) {
            this.segmentsWithPermissions.add(location.segmentId);
          }

          if (!acc[location.locationId]) {
            acc[location.locationId] = [permissions];
          } else {
            acc[location.locationId] =
              acc[location.locationId].concat(permissions);
          }
        });

        return acc;
      },
      {}
    );
    this.locationMap = locationMap;
  }

  async initPermissions(
    itemTypes: Array<SeatingItemPropertyMap>,
    opts?: {
      event?: number;
      membership_benefit?: number;
      customerGroupID?: number;
      basketHoldID?: number;
      exchangeSeatPermissions?: string | number[] | undefined;
    }
  ): Promise<void> {
    // Handle all edge cases
    if (!opts?.exchangeSeatPermissions) {
      this.exchangeSeatPermissions = [];
    } else if (typeof opts.exchangeSeatPermissions === "string") {
      this.exchangeSeatPermissions = opts.exchangeSeatPermissions
        .split(",")
        .filter(Boolean)
        .map(Number);
    } else {
      this.exchangeSeatPermissions = opts.exchangeSeatPermissions;
    }

    const seatPermissions = await this.fetchSeatPermissions({
      ...opts,
      exchangeSeatPermissions:
        typeof opts?.exchangeSeatPermissions === "string"
          ? opts.exchangeSeatPermissions
          : opts?.exchangeSeatPermissions?.join(","),
    });
    // fetch and set location permissions
    const locationPermissions: PublicBaseLocationPermissionsV2Serializer =
      await this.fetchLocationPermissions({
        ...opts,
      });

    /**
     * Create seat permissions and item type maps.
     * Used for quick data access on individual seats.
     */

    const itemMap = this.createItemTypeMap(itemTypes);

    const seatPerms: Array<{
      id: number;
      seats: Array<SeatPermissionPropertyMap>;
    }> = this.createNewSeatPermissionArray(
      seatPermissions.ticket_types ?? seatPermissions.membership_levels,
      this.seatPermissionPropertyMap
    );
    this.createSeatPermissionMap(seatPerms, itemMap);

    const locationPerms = this.createNewLocationPermissionArray(
      locationPermissions.ticket_types ?? locationPermissions.membership_levels,
      this.locationPermissionPropertyMap
    );
    this.createLocationPermissionMap(locationPerms, itemMap);
  }

  /**
   * Returns a permission object and the index of that permission from cartPermissions.
   */
  getCartPermission(
    permissionId: number
  ): (CartPermissionType & { index: number }) | undefined {
    const index = this.cartPermissions.findIndex(
      (p) => p.permissionId === permissionId
    );
    // return undefined if permission not found
    if (index === -1 || !this.cartPermissions[index]) {
      return undefined;
    }

    // return index so we can modify the object at this index
    return { ...this.cartPermissions[index], index };
  }

  /**
   * Returns a permission object and the index of that permission from pendingCartPermissions.
   */
  getPendingCartPermission(
    permissionId: number
  ): (CartPermissionType & { index: number }) | undefined {
    const index = this.pendingCartPermissions.findIndex(
      (p) => p.permissionId === permissionId
    );
    // return undefined if permission not found
    if (index === -1 || !this.pendingCartPermissions[index]) {
      return undefined;
    }

    // return index so we can modify the object at this index
    return {
      permissionId: this.pendingCartPermissions[index].permissionId,
      itemTypeId: this.pendingCartPermissions[index].itemTypeId,
      seat: this.pendingCartPermissions[index].$data,
      resaleId: this.pendingCartPermissions[index].resaleId,
      index,
    };
  }

  /**
   * Returns a seat if it is in the pending cart array
   */
  getSeatInPendingCartPermissions(
    seatId: number | Array<number>
  ): CartPermissionType | undefined {
    let findSeat: FormattedSeatType | undefined;
    // if seatId is a number, compare id values directly
    if (!Array.isArray(seatId)) {
      findSeat = this.pendingCartPermissions.find(
        (s) => s.$data && s.$data.id === seatId
      );
    }
    // else seatId is an array, check if id value exists in the array
    // return the first value found in the permissions array
    else {
      findSeat = this.pendingCartPermissions.find(
        (s) => s.$data && seatId.includes(s.$data.id)
      );
    }

    // return undefined if permission not found
    if (!findSeat) {
      return undefined;
    }
    // return data as the CartPermissionType
    return {
      permissionId: findSeat.permissionId,
      itemTypeId: findSeat.itemTypeId,
      resaleId: findSeat.resaleId,
      seat: findSeat.$data,
    };
  }

  getSeatExchangePermissions(
    seatPermissions: Array<MapPermissionsType>
  ): Array<MapPermissionsType> {
    if (!seatPermissions) return [];
    const permissionIds =
      typeof this.exchangeSeatPermissions === "string"
        ? this.exchangeSeatPermissions.split(",").filter(Boolean).map(Number)
        : this.exchangeSeatPermissions;

    return seatPermissions.filter((permission) => {
      if (permissionIds?.includes(permission.seatingItemPermissionId ?? -1)) {
        return true;
      }
      return false;
    });
  }

  /**
   * Returns a seat if it has been added to the cart
   */
  getSeatInCartPermissions(
    seatId: number | Array<number>
  ): CartPermissionType | undefined {
    let findSeat: CartPermissionType | undefined;
    // if seatId is a number, compare id values directly
    if (!Array.isArray(seatId)) {
      findSeat = this.cartPermissions.find(
        (p) => p.seat && p.seat.id === seatId
      );
    }
    // else seatId is an array, check if id value exists in the array
    // return the first value found in the permissions array
    else {
      findSeat = this.cartPermissions.find(
        (p) => p.seat && seatId.includes(p.seat.id)
      );
    }

    // return undefined if seat not found
    if (!findSeat) {
      return undefined;
    }
    return findSeat;
  }

  /**
   * Returns a location if it has been added to the cart
   */
  getLocationInCartPermissions(
    locationId: number
  ): CartPermissionType | undefined {
    const findLocation = this.cartPermissions.find(
      (p) => p.location && p.location.id === locationId
    );
    // return undefined if location not found
    if (!findLocation) {
      return undefined;
    }
    return findLocation;
  }

  /**
   * Detects whether a given seatId is available for purchase
   * Returns true if the seat is not in the cart or in the pending array
   */
  isSeatAvailable(seatId: number | Array<number>): boolean {
    if (this.seatingObjectArray.length > 0) {
      if (Array.isArray(seatId)) {
        return seatId.every(
          (id) => !this.seatingObjectArray.some((seat) => seat.id === id)
        );
      }
      return !this.seatingObjectArray.some((seat) => seat.id === seatId);
    }
    // checks the pending permissions array
    if (this.pendingCartPermissionsCount > 0) {
      // check if a single seat is available
      if (!Array.isArray(seatId)) {
        return Boolean(
          this.seatingMap[seatId] &&
            !this.getSeatInPendingCartPermissions(seatId)
        );
      }
      // check if all seats passed in are available
      return seatId.every((id) =>
        Boolean(
          this.seatingMap[id] && !this.getSeatInPendingCartPermissions(id)
        )
      );
    }

    // checks the cart permissions
    // check if a single seat is available
    if (!Array.isArray(seatId)) {
      return Boolean(
        this.seatingMap[seatId] && !this.getSeatInCartPermissions(seatId)
      );
    }
    // check if all seats passed in are available
    return seatId.every((id) =>
      Boolean(this.seatingMap[id] && !this.getSeatInCartPermissions(id))
    );
  }

  getSeatToRight(
    seatId: number | Array<number>
  ): CartPermissionType | number | undefined {
    if (this.seatingObjectArray.length > 0) {
      if (Array.isArray(seatId)) {
        return this.seatingObjectArray.find((seat) => seatId.includes(seat.id))
          ?.id;
      }
      return this.seatingObjectArray.find((seat) => seat.id === seatId)?.id;
    }
    return this.pendingCartPermissionsCount > 0
      ? this.getSeatInPendingCartPermissions(seatId)
      : this.getSeatInCartPermissions(seatId);
  }

  getSeatToLeft(
    seatId: number | Array<number>
  ): CartPermissionType | number | undefined {
    if (this.seatingObjectArray.length > 0) {
      if (Array.isArray(seatId)) {
        return this.seatingObjectArray.find((seat) => seatId.includes(seat.id))
          ?.id;
      }
      return this.seatingObjectArray.find((seat) => seat.id === seatId)?.id;
    }
    return this.pendingCartPermissionsCount > 0
      ? this.getSeatInPendingCartPermissions(seatId)
      : this.getSeatInCartPermissions(seatId);
  }

  /**
   * Detects whether an arrangment of avalable/selected seats in a given row
   * leaves a seat isolated
   *
   *      returns an array of seats to remove/add
   *      or
   *      false if there are no isolated seats
   */
  getIsolatedSeats({
    curr,
    initialAvailable = 0, // 0 = adding seat, 1 = removing seat
    consecAvailable = undefined, // amount of seats in a row that are available
    dir = undefined, // direction we are currently parsing the row 'prev' or 'next'
  }: {
    curr: number | null;
    initialAvailable?: number;
    consecAvailable?: number;
    dir?: string;
  }): Array<number> | number | undefined {
    if (!curr || !this.seatLinkedList[curr]) {
      // if curr doesn't exist in the seat linked list then return false
      // we can add isolated seats to specific locations this way
      return undefined;
    }
    // set consec available to inititial available
    // this allows us to detect whether someone is adding or removing a seat
    const initConsecAvailable = consecAvailable || initialAvailable;

    // if direction is not set, this is the first iteration
    if (!dir) {
      // check if there is a node to the left and run the algorithm on that node.
      // if there is no left node, then we don't have isolated seats to the left
      // so set to undefined
      const left =
        this.seatLinkedList[curr].prev &&
        this.getIsolatedSeats({
          curr: this.seatLinkedList[curr].prev,
          initialAvailable,
          consecAvailable: initConsecAvailable,
          dir: "prev",
        });

      // check if there is a node to the right and run the algorithm on that node.
      // if there is no right node, then we don't have isolated seats to the right
      // so set to undefined
      const right =
        this.seatLinkedList[curr].next &&
        this.getIsolatedSeats({
          curr: this.seatLinkedList[curr].next,
          initialAvailable,
          consecAvailable: initConsecAvailable,
          dir: "next",
        });

      /**
       * !! SPECIAL CASE !!
       * If we are REMOVING seats AND there ARE seats to the left & right
       * AND on either the left OR right has non-isolated seats, then
       * there is NOT an isolated seat.
       */
      if (
        initialAvailable === 1 &&
        left !== null &&
        right !== null &&
        (!left || !right)
      ) {
        return undefined;
      }

      // if there are no nodes to the left or right both values will be null and there will be no isolated seats
      // otherwise return whether or not the left or right side has isolated seats
      if (left || right) {
        const isolatedSeats: Array<number> = [];

        // removing seats
        if (initialAvailable === 1) {
          let leftSeat: CartPermissionType | number | undefined;
          let rightSeat: CartPermissionType | number | undefined;

          // get seat to the left
          if (left) {
            leftSeat = this.getSeatToLeft(left);
          }
          if (right) {
            // get seat to the right
            rightSeat = this.getSeatToRight(right);
          }

          if (left && leftSeat && typeof left === "number") {
            isolatedSeats.push(left);
          }
          if (right && rightSeat && typeof right === "number") {
            isolatedSeats.push(right);
          }
        }
        // adding seats
        else {
          if (left && typeof left === "number") {
            // if the seat is not in the seating object array, then it is isolated
            if (!this.seatingObjectArray.some((seat) => seat.id === left)) {
              isolatedSeats.push(left);
            }
          }
          if (right && typeof right === "number") {
            // if the seat is not in the seating object array, then it is isolated
            if (!this.seatingObjectArray.some((seat) => seat.id === right)) {
              isolatedSeats.push(right);
            }
          }
        }

        if (isolatedSeats.length > 0) {
          return isolatedSeats;
        }
      }

      return undefined;
    }

    let next;
    // get the next node, depending on the direction we are parsing the row
    if (dir === "next") {
      next = this.seatLinkedList[curr].next;
    } else if (dir === "prev") {
      next = this.seatLinkedList[curr].prev;
    }

    // check if the current seat we are on is available
    const currSeatAvailable = this.isSeatAvailable(curr);

    // if the seat is available then add one to the consec available count
    // otherwise keep the same
    const newConsecAvailable = currSeatAvailable
      ? initConsecAvailable + 1
      : initConsecAvailable;

    // if there is no next seat then we are at the end of the row and we should return
    // whether or not there are isolated seats
    if (!next || !currSeatAvailable) {
      /**
       * If we are at the end of the row or hit an unavailable seat and there are 0 consecutive seats that means
       * there were no gaps, so return undefined.
       * If there are less then 2 consecutive available seats and we are at the end of
       * the row or at an unavailable seat that means we have an isolated seat
       */
      if (newConsecAvailable > 0 && newConsecAvailable < 2) {
        return curr;
      }

      return undefined;
    }
    // eslint-disable-next-line
    else if (initConsecAvailable >= 2) {
      return undefined;
    }

    const isolatedSeat = this.getIsolatedSeats({
      curr: next,
      initialAvailable,
      consecAvailable: newConsecAvailable,
      dir,
    });

    if (!isolatedSeat) {
      return isolatedSeat;
    }

    return this.isSeatAvailable(isolatedSeat) ? isolatedSeat : curr;
  }

  /**
   * Gets number of seats added to cartPermissions map.
   */
  public get cartPermissionsCount(): number {
    return this.cartPermissions.reduce((count, p) => {
      let newCount = count;
      if (p.seat) {
        newCount += 1;
      }
      if (p.location) {
        newCount += p.location.count;
      }
      return newCount;
    }, 0);
  }

  /**
   * Gets number of seats in pending cart permissions
   */
  public get pendingCartPermissionsCount(): number {
    return this.pendingCartPermissions.length;
  }

  /**
   * Add seat to cartPermissions map.
   */
  addToCartPermissions({
    permissionId,
    itemTypeId,
    resaleId,
    seat,
    location,
  }: CartPermissionType): void {
    const cartPermission = this.getCartPermission(permissionId);
    if (!cartPermission) {
      this.cartPermissions.push({
        permissionId,
        itemTypeId,
        resaleId,
        // conditionally add seat and location keys
        ...(seat && { seat }),
        ...(location && { location }),
      });
    } else if (location && cartPermission.location) {
      /**
       * If we are trying to update a location and the
       * location exists in the found permission replace it with
       * the incoming data.
       * This is used to update the count.
       */
      this.cartPermissions[cartPermission.index].location = location;
    }
  }

  /**
   * Remove seat from cartPermissions array.
   */
  removeFromCartPermissions(
    permissionId: number,
    updateLocationQty?: number
  ): CartPermissionType | undefined {
    // Will only mutate array if cart permission is found
    let returnVal;

    /**
     * If cart permission is found, splice out of array and return
     * permission
     */
    for (let i = 0; i < this.cartPermissions.length; i += 1) {
      if (this.cartPermissions[i].permissionId === permissionId) {
        const cartPermission = this.cartPermissions[i];

        // for item_groups that are split in the cart,
        // update the location count when removing them one at a time
        // example: info_required tickets or memberships
        if (
          updateLocationQty &&
          updateLocationQty > 0 &&
          cartPermission.location
        ) {
          cartPermission.location.count = updateLocationQty;
          returnVal = undefined;
        }
        // else splice the permission out of the array
        else {
          this.cartPermissions.splice(i, 1);
          returnVal = cartPermission;
        }
      }
    }

    return returnVal;
  }

  initBasket(itemGroups: Array<TicketItemGroupType>, inputId: number): void {
    this.cartPermissions = [];

    // check for pending cart permissions
    // if they exist, we don't want to initialize permissions in the actual cart
    if (
      itemGroups &&
      itemGroups.length > 0 &&
      this.pendingCartPermissionsCount === 0
    ) {
      itemGroups
        .filter(
          (itemGroup) =>
            itemGroup.event === inputId ||
            itemGroup.membership_group === inputId
        )
        .forEach((itemGroup) => {
          const { permissions, location_permissions } = itemGroup;

          if (permissions) {
            permissions.forEach(({ id, seat, resale_submission_id }) =>
              this.addToCartPermissions({
                permissionId: id,
                seat,
                resaleId: resale_submission_id, // persist resaleId between page refreshes
                location: undefined,
                itemTypeId: itemGroup.type,
              })
            );
          }
          if (location_permissions) {
            location_permissions.forEach(({ id, location }) => {
              this.addToCartPermissions({
                permissionId: id,
                itemTypeId: itemGroup.type,
                resaleId: undefined, // resaleId not needed for locations
                seat: undefined,
                location: {
                  id: location.id,
                  count: location.count,
                },
              });
            });
          }
        });
    }
  }

  /**
   *
   * Sets initial cart permissions from permissions array instead of basket. This is used when you are not adding selected
   * items to a basket. Pass in previously selected permissions to set the initial cart permissions.
   *
   * Used with group sales assigned seating when the seating map is unmounted and remounted.
   *
   * @param permissions
   */
  initSelectedPermissions(permissions: Array<CartPermissionType> = []): void {
    this.cartPermissions = [];

    permissions.forEach((item) => {
      if (item.seat && !this.getSeatInCartPermissions(item.seat.id)) {
        this.addToCartPermissions({
          itemTypeId: item.itemTypeId,
          permissionId: item.permissionId,
          seat: item.seat,
          location: undefined,
          resaleId: undefined,
        });
      }

      if (
        item.location &&
        !this.getLocationInCartPermissions(item.location.id)
      ) {
        this.addToCartPermissions({
          itemTypeId: item.itemTypeId,
          permissionId: item.permissionId,
          seat: undefined,
          location: item.location,
          resaleId: undefined,
        });
      }
    });
  }

  /**
   * Sorts permissions in the itemTypeMap based on the sort_order or id fields
   */
  sortItemTypeMap(
    a: { seatingItemTypeId: number },
    b: { seatingItemTypeId: number }
  ): number {
    let sortA = 0;
    let sortB = 0;

    // for events, sort permissions by sort_order
    if (this.mapItemType === common.SeatingMapItemType.EVENT) {
      sortA = this.itemTypeMap[a.seatingItemTypeId]?.sort_order ?? 0;
      sortB = this.itemTypeMap[b.seatingItemTypeId]?.sort_order ?? 0;
    }
    // TODO: update logic when membership sort_order is defined
    // for memberships, sort_order is always 0, so sort by id instead
    else if (this.mapItemType === common.SeatingMapItemType.MEMBERSHIP) {
      sortA = this.itemTypeMap[a.seatingItemTypeId]?.id ?? 0;
      sortB = this.itemTypeMap[b.seatingItemTypeId]?.id ?? 0;
    }

    return sortA - sortB;
  }

  /**
   * Sorts an input seating item object based on the sort_order or id fields
   * mapItemType is a param to keep this function static
   */
  static sortPermissions(
    a: SeatingItemPropertyMap,
    b: SeatingItemPropertyMap,
    mapItemType: common.SeatingMapItemType
  ): number {
    let sortA = 0;
    let sortB = 0;

    // for events, sort permissions by sort_order
    if (mapItemType === common.SeatingMapItemType.EVENT) {
      sortA = a.sort_order ?? 0;
      sortB = b.sort_order ?? 0;
    }
    // TODO: update logic when membership sort_order is defined
    // for memberships, sort_order is always 0, so sort by id instead
    else if (mapItemType === common.SeatingMapItemType.MEMBERSHIP) {
      sortA = a.id ?? 0;
      sortB = b.id ?? 0;
    }

    return sortA - sortB;
  }

  async resetSeatPermissions(seat: {
    $data: SeatWithLocationDataType;
  }): Promise<Array<MapPermissionsType>> {
    const exchangeCredit = isStorageAvailable("localStorage")
      ? localStorage.getItem("exchangeCreditData")
      : null;
    const exchangeCreditData = JSON.parse(exchangeCredit || "{}");
    let promise: Array<PublicTTSeatPermissionSerializer> = [];
    // add item type permissions back to permissions map
    // permissions do not exist so try to fetch them
    if (this.spaceId) {
      const params: PublicTTPermissionsPayloadType = {
        seat: seat.$data.id,
        space: this.spaceId,
        ticket_type__event: undefined,
        membership_benefit: undefined,
        basket_hold: this.basketHoldID,
        membership_level_ticket_types:
          exchangeCreditData?.is_membership_ticket_exchange,
      };

      if (
        this.seatingObjectId &&
        this.mapItemType === common.SeatingMapItemType.EVENT
      ) {
        params.ticket_type__event = this.seatingObjectId;
      } else if (
        this.seatingObjectId &&
        this.mapItemType === common.SeatingMapItemType.MEMBERSHIP
      ) {
        params.membership_benefit = this.seatingObjectId;
      }

      promise = await this.seatPermissionsRepo.getPermissions({ params });
    }

    let hasPermissionWithResaleId = false;

    /**
     * if item types returned, map returned data to match new
     * query and sort by item type order
     */
    const $permissions: Array<MapPermissionsType> = promise
      .map(
        ({
          id,
          ticket_type,
          membership_level,
          resale_submission_id,
          resale_submission_status,
        }) => {
          const returnedItemId = ticket_type ?? membership_level;
          // check for permissions with a resale id
          if (resale_submission_id) {
            hasPermissionWithResaleId = true;
          }
          return {
            seatingItemTypeId: returnedItemId,
            seatingItemPermissionId: id,
            seatingItemMinPurchaseLimit:
              this.itemTypeMap[returnedItemId]?.minimum_purchase_limit,
            seatingItemResaleId: resale_submission_id,
            seatingItemResaleStatus: resale_submission_status,
          };
        }
      )
      .filter(({ seatingItemTypeId, seatingItemResaleId }) => {
        // if a permission has a resale id,
        // only return that permission
        if (hasPermissionWithResaleId) {
          return this.itemTypeMap[seatingItemTypeId] && seatingItemResaleId;
        }
        // default: return permissions that exist in the itemTypeMap
        return this.itemTypeMap[seatingItemTypeId];
      })
      .sort(this.sortItemTypeMap);

    // add permission to seating map
    this.seatingMap[seat.$data.id] = $permissions;

    // add seat ids to resale map
    const resaleId = $permissions.find(
      (p) => p.seatingItemResaleId
    )?.seatingItemResaleId;
    if (resaleId) {
      this.createResaleMap(resaleId, seat.$data.id);
    }

    return $permissions;
  }

  /**
   * Resets seat permissions on the itemTypeMap when removing seats from the cart
   * @param {Array} seats
   */
  resetMultipleSeatPermissions(
    seats: Array<{ $data: SeatWithLocationDataType }>
  ): void {
    for (let i = 0; i < seats.length; i += 1) {
      this.resetSeatPermissions(seats[i]);
    }
  }

  deleteSeatPermissions(seat: { $data: SeatWithLocationDataType }): void {
    delete this.seatingMap[seat.$data.id];
  }

  deleteMultipleSeatPermissions(
    seats: Array<{ $data: SeatWithLocationDataType }>
  ): void {
    for (let i = 0; i < seats.length; i += 1) {
      this.deleteSeatPermissions(seats[i]);
    }
  }

  // Function to create generic objects with the fields of a propertyMap object
  static getMappedProperties<T extends Record<string, unknown>, S>(
    inputPropertyMap: T,
    inputItem: S
  ) {
    let mappedProperties = {} as T;

    // generate mappedProperties with initialized/default field values
    Object.keys(inputPropertyMap).forEach((key: string) => {
      const originalPropertyName = inputPropertyMap[key] as string;

      if (originalPropertyName) {
        const returnVal = get(inputItem, originalPropertyName, undefined);
        mappedProperties = { ...mappedProperties, [key]: returnVal };
      }
    });
    return mappedProperties;
  }

  // Define a function to create the new map object based on input and propertyMap
  // example input: seat permissions for ticket_types or membership_levels
  createNewLocationPermissionArray = (
    inputArray:
      | PublicBaseLocationPermissionsV2Serializer["ticket_types"]
      | PublicBaseLocationPermissionsV2Serializer["membership_levels"],
    inputPropertyMap?: LocationPermissionPropertyMap
  ): Array<{ id: number; locations: Array<LocationPermissionPropertyMap> }> => {
    const arrayObj = inputArray ?? [];
    return arrayObj.map((input) => {
      const formattedLocs = input.locations.map(
        (
          location:
            | TicketLocationPermissionType
            | MembershipLocationPermissionType
        ) => {
          // create object of type SeatPermissionPropertyMap
          return SeatingService.getMappedProperties<
            LocationPermissionPropertyMap,
            typeof location
          >(inputPropertyMap ?? this.locationPermissionPropertyMap, location);
        }
      );
      return { id: input.id, locations: formattedLocs };
    });
  };

  // Define a function to create the new map object based on input and propertyMap
  // example input: seat permissions for ticket_types or membership_levels
  createNewSeatPermissionArray = (
    inputArray:
      | PublicBaseSeatPermissionsV2Serializer["ticket_types"]
      | PublicBaseSeatPermissionsV2Serializer["membership_levels"],
    inputPropertyMap?: SeatPermissionPropertyMap
  ): Array<{ id: number; seats: Array<SeatPermissionPropertyMap> }> => {
    const arrayObj = inputArray ?? [];
    return arrayObj.map((input) => {
      const formattedSeats = input.seats.map(
        (seat: TicketSeatPermissionType | MembershipSeatPermissionType) => {
          // create object of type SeatPermissionPropertyMap
          return SeatingService.getMappedProperties<
            SeatPermissionPropertyMap,
            typeof seat
          >(inputPropertyMap ?? this.seatPermissionPropertyMap, seat);
        }
      );

      return { id: input.id, seats: formattedSeats };
    });
  };

  getSelectedSeatIds(): number[] {
    return this.seatingObjectArray.map((seat) => seat.id);
  }

  clearSelectedSeatIds(): void {
    this.seatingObjectArray = [];
  }

  removeSeatFromSelectedSeatIds(seatId: number): void {
    this.seatingObjectArray = this.seatingObjectArray.filter(
      (seat) => seat.id !== seatId
    );
  }

  addSeatToSeatingObjectArray(seat: SeatWithLocationDataType): void {
    if (
      !this.seatingObjectArray.some(
        (existingSeat) => existingSeat.id === seat.id
      )
    ) {
      this.seatingObjectArray.push(seat);
    }
  }

  getSeatingObjectArray(): Array<SeatWithLocationDataType> {
    return [...this.seatingObjectArray];
  }

  clearSeatingObjectArray(): void {
    this.seatingObjectArray = [];
  }

  removeSeatFromSeatingObjectArray(seatId: number): void {
    this.seatingObjectArray = this.seatingObjectArray.filter(
      (seat) => seat.id !== seatId
    );
  }

  // Define a function to create the new map itemType array based on input and propertyMap
  // example: ticket types, membership levels
  createNewItemTypeArray = (
    inputArray:
      | Array<PublicTicketTypeSerializer>
      | Array<VenueTicketTypeSerializer>
      | Array<PublicMembershipLevelSerializer>
      | Array<VenueBasedMembershipLevelSerializer>,
    inputPropertyMap?: SeatingItemPropertyMap
  ): Array<SeatingItemPropertyMap> => {
    return inputArray.map((input) => {
      // create object of type SeatingItemPropertyMap
      return SeatingService.getMappedProperties<
        SeatingItemPropertyMap,
        typeof input
      >(inputPropertyMap ?? this.itemArrayPropertyMap, input);
    });
  };

  // Define a function to create the new map object based on input and propertyMap
  // example input: event, membership group
  createNewSeatingObject = (
    inputObject:
      | PublicBaseEventSerializer
      | VenueBasedEventSerializer
      | PublicMembershipGroupSerializer
      | VenueBasedMembershipGroupSerializer,
    inputPropertyMap?: SeatingEventPropertyMap
  ): SeatingEventPropertyMap => {
    // create object of type SeatingEventPropertyMap
    return SeatingService.getMappedProperties<
      SeatingEventPropertyMap,
      typeof inputObject
    >(inputPropertyMap ?? this.seatingObjectPropertyMap, inputObject);
  };
}
